import { initializeControls } from '@atlassian/browser-storage-controls/controllers/cookie-controls/initialize-controls';
import type { BscInitializationResult } from '@atlassian/browser-storage-controls/types';

const BSC_FEATURE_GATE = 'csm-cm04-use-atlassian-browser-storage-controls';
// Soaking gate: enables webStorage + indexedDB consent enforcement.
const BSC_WEBSTORAGE_INDEXEDDB_GATE = 'csm-cm04-bsc-webstorage-indexeddb';
// Soaking gate: analytics-only mode, disables webStorage + indexedDB consent checks.
const BSC_ANALYTICS_ONLY_GATE = 'csm-cm04-bsc-analytics-only';
// Phase-4 gate: when true, blocks uncategorized web-storage/indexedDB keys (disables the allow-uncategorized overrides).
const BSC_BLOCK_UNCATEGORIZED_GATE = 'csm-cm04-bsc-block-uncategorized';

// On embed sites `window.__PLATFORM_FEATURE_FLAGS__` is unset so this returns false and BSC init is skipped.
const atlassianFG = (key: string): boolean => {
	try {
		return Boolean(window.__PLATFORM_FEATURE_FLAGS__?.booleanResolver?.(key));
	} catch (error) {
		return false;
	}
};

// Synchronous FG check so callers can avoid introducing async/await when BSC is disabled.
export const isBrowserStorageControlsEnabled = (): boolean => atlassianFG(BSC_FEATURE_GATE);

// Performance.now is not supported in opera mini, so guard it.
const perfNow = (): number => {
	try {
		return performance.now();
	} catch (e) {
		return 0;
	}
};

type InitializeBrowserStorageControlsArgs = {
	cloudId: string;
	userId?: string;
};

type InitializeBscReturnType = BscInitializationResult | undefined;

// Initialize BSC before SessionManager cookie access; shared by the iframe and host realms.
export const initializeBrowserStorageControls = async ({
	cloudId,
	userId,
}: InitializeBrowserStorageControlsArgs): Promise<InitializeBscReturnType> => {
	if (!atlassianFG(BSC_FEATURE_GATE)) {
		return;
	}

	let initResult: InitializeBscReturnType;

	const webStorageIndexedDbGate = atlassianFG(BSC_WEBSTORAGE_INDEXEDDB_GATE);
	const analyticsOnlyGate = atlassianFG(BSC_ANALYTICS_ONLY_GATE);
	const blockUncategorizedGate = atlassianFG(BSC_BLOCK_UNCATEGORIZED_GATE);
	// Either soaking gate enables storage; consent checks are only disabled during analytics-only soaking.
	const webStorageIndexedDbEnabled = webStorageIndexedDbGate || analyticsOnlyGate;
	const analyticsOnly = analyticsOnlyGate && !webStorageIndexedDbGate;
	// Phase-4: uncategorized keys are only allowed while storage is enabled and the block gate is FAIL.
	const allowUncategorizedItems = webStorageIndexedDbEnabled && !blockUncategorizedGate;

	const beforeInit = perfNow();
	try {
		initResult = await initializeControls({
			product: 'jira',
			webStorageEnabled: webStorageIndexedDbEnabled,
			indexedDbEnabled: webStorageIndexedDbEnabled,
			disableWebStorageConsentChecks: analyticsOnly,
			disableIndexedDbConsentChecks: analyticsOnly,
			allowUncategorizedItemsInWebStorageOverrides: allowUncategorizedItems,
			allowUncategorizedItemsInIndexedDBOverrides: allowUncategorizedItems,
			cloudId,
			userId,
		});
	} catch (bscErr) {
		// eslint-disable-next-line no-console
		console.error('[FAILED TO INITIALIZE BROWSER STORAGE CONTROLS]', bscErr);
	}

	// eslint-disable-next-line no-console
	console.info(
		`[initializeBrowserStorageControls duration] ${perfNow() - beforeInit} milliseconds`,
	);

	return initResult;
};
